// @flow
import last from 'lodash/fp/last';
import sortBy from 'lodash/fp/sortBy';
/* 💁 this is intentionally not from lodash/fp because fp.round does not
support the second precision argument */
import round from 'lodash/round';

export type CssThemeValue = mixed;

export type ColorValueMap = any;

export const colors = {
  white: '#FFFFFF',
  gray_1: '#F6F6F8',
  gray_2: '#DADCDD',
  gray_3: '#ACAEAF',
  gray_4: '#989B9C',
  gray_5: '#57595A',
  gray_6: '#3D3E3F',
  gray_7: '#EEF1F4',
  black: '#000',
  overlay: 'rgba(23,23,23,0.7)',
  scrim: 'linear-gradient(rgba(23,23,23,0), rgba(23,23,23,0.7))',
  reverse_scrim: 'linear-gradient(rgba(23,23,23,0.7), rgba(23,23,23,0))',
  dark_scrim:
    'linear-gradient(-180deg, rgba(0, 0, 0, 0) 0%, rgba(0, 0, 0, 0.55) 46%, rgba(0, 0, 0, 0.92) 100%)',
  red: '#DA1E28',
  red_hover: '#B61921',
  red_active: '#92141B',
  green: '#16A840',
  blue: '#0062FF',
  yellow: '#EFC100',
  yellow_2: '#FFF3BF',
  go_green: '#a7f0ba',
  medium_yellow: '#ffe082',
  stop_red: '#ffd7d9',
  go_green_text: '#0e6027',
  medium_yellow_text: '#4e2709',
  stop_red_text: '#a2191f',
  // the two following will be overwritten, just needed them here to make flow happy
  brand: '#4D33FF',
  brand_active: '',
  brand_hover: '',
  brand_text: '#FFFFFF',
};

export type Color = $Keys<typeof colors>;

export type Theme = $ReadOnly<{
  'font-family': string,
  'legacy-grey': ColorValueMap,
  'legacy-green': ColorValueMap,
  'legacy-oliveGreen': ColorValueMap,
  'legacy-red': ColorValueMap,
  'legacy-yellow': ColorValueMap,
  box_shadow: CssThemeValue,
  suggestionTextSize: CssThemeValue,
  suggestionTextHeaderSize: CssThemeValue,
  elevations: Map<number, string>,
  [colorName: Color]: string,
}>;

export const elevations: Map<number, string> = new Map<number, string>([
  [0, '0 0px 0px 0 rgba(0, 0, 0, 0.0), 0 0px 0px 0 rgba(0, 0, 0, 0.0)'],
  [1, '0 1px 3px 0 rgba(0, 0, 0, 0.1), 0 1px 2px 0 rgba(0, 0, 0, 0.2)'],
  [2, '0 3px 6px 0 rgba(0, 0, 0, 0.16), 0 3px 6px 0 rgba(0, 0, 0, 0.23)'],
  [3, '0 10px 20px 0 rgba(0, 0, 0, 0.19), 0 6px 6px 0 rgba(0, 0, 0, 0.26)'],
  [4, '0 14px 28px 0 rgba(0, 0, 0, 0.25), 0 10px 10px 0 rgba(0, 0, 0, 0.26)'],
  [5, '0 19px 38px 0 rgba(0, 0, 0, 0.3), 0 15px 12px 0 rgba(0, 0, 0, 0.22)'],
]);

export type Elevation = 0 | 1 | 2 | 3 | 4 | 5;

export const elevation = ({
  theme,
  elevation: val,
}: {
  theme: Theme,
  elevation: Elevation,
}): string => {
  const shadow = theme.elevations.get(val);
  return shadow ? `box-shadow: ${shadow}` : '';
};

export const theme: Theme = {
  ...colors,
  elevations,
  /* 💁 Using computed keys below is a hack around flow's not
    allowing non-string literal property keys:
    https://github.com/facebook/flow/issues/380#issuecomment-224380551
 */
  /* eslint-disable no-useless-computed-key */
  'legacy-grey': {
    [50]: '#fafafa',
    [100]: '#f4f4f4',
    [200]: '#eee',
    [300]: '#d8d8d8',
    [400]: '#b1b1b1',
    [500]: '#9e9e9e',
    [600]: '#818181',
    [700]: '#616161',
    [800]: '#424242',
    [900]: '#28292c',
    [950]: '#151615',
  },
  'legacy-green': {
    [500]: '#66c660',
    [700]: '#008000',
  },
  'legacy-oliveGreen': {
    [100]: '#f7ffee',
    [200]: '#c0dca6',
    [300]: '#93c958',
    [600]: '#73ab36',
    [800]: '#497f0d',
  },
  'legacy-red': {
    [500]: '#e92f29',
    [600]: '#ce1900',
  },
  'legacy-yellow': {
    [500]: '#efc236',
  },
  /* eslint-enable no-useless-computed-key */
  suggestionTextHeaderSize: '18px',
  suggestionTextSize: '14px',
  'font-family':
    '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
  box_shadow: '0 1px 5px rgba(0, 0, 0, 0.25)',
};

export const ellipsize = (width: ?string): string => `
  ${width ? `width: ${width};` : ''}
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const multiLineEllipsize = (lines: number): string => `
  display: -webkit-inline-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: ${lines};
  overflow: hidden;
`;

type FontStyle = {|
  size: number,
  lineHeight: number,
  letterSpacing: number | 'normal',
  uppercase: boolean,
|};

export const fontSmooth = (): string => `
  -webkit-font-smoothing: antialiased;
  font-smooth: antialiased;
`;

export const fontStyles: Map<number, FontStyle> = new Map<number, FontStyle>([
  [
    1,
    {
      size: 10,
      lineHeight: 1.4,
      letterSpacing: 0.5,
      uppercase: true,
    },
  ],
  [
    2,
    {
      size: 12,
      lineHeight: 4 / 3,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    3,
    {
      size: 14,
      lineHeight: 10 / 7,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    4,
    {
      size: 16,
      lineHeight: 1.25,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    5,
    {
      size: 20,
      lineHeight: 1.2,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    5.5,
    {
      size: 24,
      lineHeight: 1.2,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    6,
    {
      size: 28,
      lineHeight: 8 / 7,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    7,
    {
      size: 40,
      lineHeight: 6 / 5,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    7.5,
    {
      size: 36,
      lineHeight: 6 / 5,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    8,
    {
      size: 48,
      lineHeight: 6 / 5,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
  [
    9,
    {
      size: 72,
      lineHeight: 6 / 5,
      letterSpacing: 'normal',
      uppercase: false,
    },
  ],
]);

export type FontSize = 1 | 2 | 3 | 4 | 5 | 5.5 | 6 | 7 | 7.5 | 8 | 9;

// this is a utility so font styles can easily be used in react style objects, such as
// the styling in react-select
export const camelCasedFontSize = (
  num: FontSize,
): {
  fontSize?: string,
  lineHeight?: number,
  letterSpacing?: string,
  textTransform?: string,
} => {
  const fs = fontStyles.get(num);
  return fs
    ? {
        fontSize: `${fs.size}px`,
        lineHeight: (round(fs.lineHeight, 2): number),
        letterSpacing: `${fs.letterSpacing}${
          typeof fs.letterSpacing === 'number' ? 'px' : ''
        }`,
        ...(fs.uppercase ? { textTransform: 'uppercase' } : { ...null }),
      }
    : { ...null };
};

export const fontSize = ({ fSize }: { fSize: FontSize }): string => {
  const fontStyle = fontStyles.get(fSize);

  if (!fontStyle) return '';

  return `
  font-size: ${fontStyle.size}px;
  line-height: ${round(fontStyle.lineHeight, 2)};
  letter-spacing: ${fontStyle.letterSpacing}${
    typeof fontStyle.letterSpacing === 'number' ? 'px' : ''
  };
  ${fontStyle.uppercase ? 'text-transform: uppercase;' : ''}
`;
};

export const fontColor = ({
  fColor,
  theme: t,
}: {
  fColor: Color,
  theme: Theme,
}): string =>
  // ` || fColor` in the function body is just for handling legacy colors
  // it will be gone soon
  fColor ? `color: ${t[fColor] || fColor};` : '';

export type FontWeight = 'normal' | 'semibold';
export const fontWeight = ({ weight }: { weight?: FontWeight }): string => {
  if (!weight) return '';
  return `font-weight: ${weight === 'semibold' ? '600' : weight};`;
};

export const fontItalic = ({ italic }: { italic?: boolean }): string => {
  if (!italic) return '';
  return `font-style: ${italic ? 'italic' : 'normal'};`;
};

export const fontDecoration = ({
  underline,
  strikethrough,
}: {
  underline?: boolean,
  strikethrough?: boolean,
}): string => {
  if (!underline && !strikethrough) return '';
  return `text-decoration: ${[
    underline ? 'underline' : null,
    strikethrough ? 'line-through' : null,
  ]
    .filter(Boolean)
    .join(' ')};`;
};

type Breakpoints = { [string]: number };

const iteratees: ((any) => number)[] = [([, value]) => value];

export const breakpointer = (
  breakpoints: Breakpoints = { ...null },
): ((target: number) => string | null) => {
  const inValueOrder = sortBy(iteratees)(
    ((Object.entries(breakpoints): any): Array<[string, number]>),
  );
  return (target: number): string | null =>
    (last(inValueOrder.filter(([, value]) => value <= target)) || [])[0] ||
    null;
};

// Hide scrollbar without removing scrolling functionality that would happen
// with overflow: hidden
export const hideScrollbar = (): string => `
  overflow: auto;
  scrollbar-width: none;
  ::-webkit-scrollbar {
    width: 0 !important;
  }
  -ms-overflow-style: none;
`;

export const defaultPresetColors = [
  '#FFFFFF',
  '#ABABAB',
  '#000000',
  '#148CFF',
  '#51C321',
  '#F5CF00',
  '#FFA625',
  '#FF0000',
  '#BB0000',
  '#751988',
];

export default theme;
